// src/config/dashboardNav.js
// Maxula
// Index des pages des tableaux de bord (Admin / Marchand) + moteur de
// recherche "intelligent" (insensible aux accents/casse, prefixe, sous-chaine,
// sous-sequence/typo) pour la barre de recherche de la topbar.

import {
  FiHome, FiPackage, FiShoppingBag, FiTag, FiGift, FiMessageCircle,
  FiUsers, FiDollarSign, FiFileText, FiCreditCard, FiShield, FiBell,
  FiActivity, FiSettings, FiCode, FiLifeBuoy, FiMapPin, FiLayers,
  FiBarChart2, FiMessageSquare, FiTrendingUp, FiMail, FiImage,
  FiAlertTriangle, FiDownloadCloud, FiUser
} from 'react-icons/fi';

// Normalisation : minuscules, sans accents, sans ponctuation.
const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// "abc" est-il une sous-sequence de "hay" ? (tolerance fautes de frappe)
const isSubsequence = (q, hay) => {
  let i = 0;
  for (let j = 0; j < hay.length && i < q.length; j += 1) {
    if (hay[j] === q[i]) i += 1;
  }
  return i === q.length;
};

// Construit une entree avec champs normalises pre-calcules.
const entry = (label, path, icon, keywords = []) => {
  const _hay = norm([label, ...keywords].join(' '));
  return {
    label,
    path,
    icon,
    _label: norm(label),
    _hay,
    _words: _hay.split(' ').filter(Boolean)
  };
};

export const DASHBOARD_NAV = {
  merchant: [
    entry('Tableau de bord', '/merchant/dashboard', FiHome, ['accueil', 'home', 'dashboard', 'bord']),
    entry('Mes Deals', '/merchant/deals', FiPackage, ['deals', 'produits', 'offres', 'annonces', 'articles']),
    entry('Mes commandes', '/merchant/orders', FiShoppingBag, ['commandes', 'orders', 'ventes', 'achats']),
    entry('Coupons', '/merchant/vouchers', FiTag, ['coupons', 'vouchers', 'bons', 'tickets']),
    entry('Codes Promo', '/merchant/promos', FiTag, ['codes promo', 'promo', 'promotion', 'reduction', 'code', 'codes']),
    entry('Récompenses', '/merchant/rewards', FiGift, ['recompenses', 'rewards', 'fidelite', 'points', 'cadeaux']),
    entry('Messages', '/merchant/messages', FiMessageCircle, ['messages', 'chat', 'discussion', 'conversations']),
    entry('Revenus', '/merchant/wallet', FiDollarSign, ['revenus', 'wallet', 'portefeuille', 'argent', 'solde', 'gains']),
    entry('Factures', '/merchant/orders?tab=invoices', FiFileText, ['factures', 'invoices', 'facturation']),
    entry('Mon Abonnement', '/merchant/subscription', FiCreditCard, ['abonnement', 'subscription', 'pack', 'plan', 'forfait']),
    entry('Vérification', '/merchant/kyc', FiShield, ['verification', 'kyc', 'identite', 'documents', 'validation']),
    entry('Équipe', '/merchant/team', FiUsers, ['equipe', 'team', 'membres', 'collaborateurs', 'staff']),
    entry('Notifications', '/merchant/notifications', FiBell, ['notifications', 'alertes', 'rappels']),
    entry('Historique', '/merchant/activity', FiActivity, ['historique', 'activite', 'logs', 'journal']),
    entry('Paramètres', '/merchant/settings', FiSettings, ['parametres', 'settings', 'reglages', 'profil', 'compte', 'config']),
    entry('API & Webhooks', '/merchant/developer', FiCode, ['api', 'webhooks', 'developpeur', 'integration', 'cle']),
    entry('Support', '/merchant/tickets', FiLifeBuoy, ['support', 'tickets', 'aide', 'assistance', 'sav'])
  ],
  admin: [
    entry('Tableau de bord', '/admin', FiHome, ['accueil', 'home', 'dashboard', 'bord']),
    entry('Gestion des Deals', '/admin/deals', FiPackage, ['deals', 'produits', 'offres', 'gestion', 'annonces']),
    entry('Commandes', '/admin/orders', FiShoppingBag, ['commandes', 'orders', 'ventes']),
    entry('Utilisateurs', '/admin/users', FiUsers, ['utilisateurs', 'users', 'clients', 'membres', 'comptes']),
    entry('Marchands', '/admin/merchants', FiTag, ['marchands', 'merchants', 'vendeurs', 'partenaires', 'agences']),
    entry('Vérifications', '/admin/verifications', FiShield, ['verifications', 'kyc', 'validation', 'identite']),
    entry('Tickets', '/admin/tickets', FiLifeBuoy, ['tickets', 'support', 'aide', 'assistance']),
    entry('Signalements', '/admin/reports', FiAlertTriangle, ['signalements', 'reports', 'reclamations', 'abus', 'plaintes']),
    entry('Leads', '/admin/leads', FiUsers, ['leads', 'prospects', 'contacts']),
    entry('Publicités', '/admin/ads', FiImage, ['publicites', 'ads', 'pubs', 'bannieres', 'annonces pub']),
    entry('API & Webhooks', '/admin/api', FiCode, ['api', 'webhooks', 'developpeur', 'integration']),
    entry('Packs', '/admin/plans', FiLayers, ['packs', 'plans', 'forfaits', 'tarifs']),
    entry('Abonnements', '/admin/subscriptions', FiCreditCard, ['abonnements', 'subscriptions']),
    entry('Vouchers', '/admin/vouchers', FiTag, ['vouchers', 'coupons', 'bons']),
    entry('Codes Promo', '/admin/promos', FiTag, ['codes promo', 'promo', 'code', 'codes', 'reduction', 'promotion']),
    entry('Récompenses', '/admin/rewards', FiGift, ['recompenses', 'rewards', 'fidelite', 'points']),
    entry('Catégories', '/admin/categories', FiTag, ['categories', 'rubriques', 'classification']),
    entry('Villes', '/admin/cities', FiMapPin, ['villes', 'cities', 'localisation', 'regions']),
    entry('Performance', '/admin/performance', FiBarChart2, ['performance', 'stats', 'indicateurs']),
    entry('Avis', '/admin/reviews', FiMessageSquare, ['avis', 'reviews', 'notes', 'commentaires', 'evaluations']),
    entry('Paiements', '/admin/payments', FiDollarSign, ['paiements', 'payments', 'transactions']),
    entry('Factures', '/admin/orders?tab=invoices', FiFileText, ['factures', 'invoices', 'facturation']),
    entry('Retraits', '/admin/withdrawals', FiDownloadCloud, ['retraits', 'withdrawals', 'versements']),
    entry('Analyses', '/admin/analytics', FiTrendingUp, ['analyses', 'analytics', 'statistiques', 'stats']),
    entry('Email Marketing', '/admin/marketing', FiMail, ['email marketing', 'emailing', 'campagnes', 'newsletter', 'mailing']),
    entry('Paramètres', '/admin/settings', FiSettings, ['parametres', 'settings', 'reglages', 'config']),
    entry('Audit Logs', '/admin/audit', FiActivity, ['audit', 'logs', 'journal', 'historique', 'traces']),
    entry('Mon profil', '/admin/profile', FiUser, ['profil', 'compte', 'profile', 'mon compte'])
  ]
};

// Score d'une entree pour une requete normalisee (chaque token doit matcher).
const scoreEntry = (qn, e) => {
  const tokens = qn.split(' ').filter(Boolean);
  if (!tokens.length) return 0;
  let score = 0;
  for (const tk of tokens) {
    if (e._label === tk) { score += 100; continue; }
    if (e._label.startsWith(tk)) { score += 70; continue; }
    if (e._words.some((w) => w.startsWith(tk))) { score += 55; continue; }
    if (e._hay.includes(tk)) { score += 40; continue; }
    if (tk.length >= 3 && isSubsequence(tk, e._hay)) { score += 15; continue; }
    return 0; // ce token n'a rien matche -> on rejette l'entree
  }
  if (e._label.startsWith(qn)) score += 25; // bonus prefixe complet
  return score;
};

// Recherche "intelligente" des pages du dashboard pour un scope donne.
export const searchPages = (scope, term, limit = 6) => {
  const list = DASHBOARD_NAV[scope];
  if (!list) return [];
  const qn = norm(term);
  if (qn.length < 1) return [];
  return list
    .map((e) => ({ e, s: scoreEntry(qn, e) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.e);
};
